<template>
  <PageLayout>
    <template #headerTitle>Gestion des coupons</template>

    <VBusHelper :emitOnMounted="$constants.EVENTS.COUPON.LIST_ALL.REFRESH" />

    <!-- HEADER WITH CREATE BUTTON -->
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center gap-4">
        <div class="text-gray-600">
          {{ $stores.coupon?.state?.list_all?.list?.length || 0 }} coupon(s)
        </div>
        <button
          class="px-4 py-2 rounded-xl bg-slate-200 text-gray-700 text-sm font-medium hover:bg-slate-300 transition-all flex items-center gap-2"
          @click="$router.push('/coupons/settings')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Paramètres
        </button>
      </div>
      <button
        class="px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 transition-all"
        @click="$bus.emit($constants.EVENTS.COUPON.CREATE_OR_UPDATE.SHOW, null)"
      >
        + Nouveau coupon
      </button>
    </div>

    <!-- ERROR STATE -->
    <CouponLoadingError
      v-if="$stores.coupon?.state?.list_all?.error"
      @refresh="() => $bus.emit($constants.EVENTS.COUPON.LIST_ALL.REFRESH)"
    >
      <template #errorDescription>
        {{ $stores.coupon?.state?.list_all?.error }}
      </template>
    </CouponLoadingError>

    <!-- COUPONS GRID -->
    <div
      v-else-if="$stores.coupon?.state?.list_all?.list?.length"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      <div
        v-for="(coupon, index) in $stores.coupon?.state?.list_all?.list"
        :key="coupon.id || index"
        class="bg-slate-100 rounded-2xl overflow-hidden group hover:shadow-lg transition-all"
      >
        <!-- IMAGE PREVIEW -->
        <div class="relative h-48 bg-gray-200">
          <ImgComponent
            :src="coupon.pictureUrl"
            alt="Coupon"
            class="w-full h-full object-cover"
          />
          <!-- PREMIUM BADGE -->
          <div
            v-if="coupon.isPremium"
            class="absolute top-3 right-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Premium
          </div>
          <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-end justify-center pb-4">
            <button
              class="px-6 py-2 rounded-xl bg-white text-gray-900 font-medium hover:bg-gray-100 transition-all"
              @click="$bus.emit($constants.EVENTS.COUPON.CREATE_OR_UPDATE.SHOW, coupon)"
            >
              Modifier
            </button>
          </div>
        </div>

        <!-- COUPON INFO -->
        <div class="p-4 space-y-3">
          <!-- TITLE -->
          <div v-if="coupon.title">
            <div class="text-xs text-gray-500">Titre</div>
            <div class="font-medium text-gray-900 truncate">{{ coupon.title }}</div>
          </div>

          <!-- DESCRIPTION -->
          <div v-if="coupon.description">
            <div class="text-xs text-gray-500">Description</div>
            <div class="text-sm text-gray-700 line-clamp-2">{{ coupon.description }}</div>
          </div>

          <!-- CODE -->
          <div v-if="coupon.code">
            <div class="text-xs text-gray-500">Code coupon</div>
            <div class="font-mono text-sm font-bold text-primary-600 truncate">{{ coupon.code }}</div>
          </div>

          <!-- ACTIONS -->
          <div class="flex gap-2 pt-2 border-t border-gray-200">
            <button
              class="flex-1 px-4 py-2 rounded-lg bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-all"
              @click="$bus.emit($constants.EVENTS.COUPON.CREATE_OR_UPDATE.SHOW, coupon)"
            >
              Modifier
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- LOADING STATE -->
    <div
      v-else-if="$stores.coupon?.state?.list_all?.loading"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="bg-slate-100 rounded-2xl overflow-hidden shimmerContainer"
      >
        <div class="h-48 bg-gray-200 shimmerTarget"></div>
        <div class="p-4 space-y-3">
          <div class="h-4 bg-gray-200 rounded shimmerTarget"></div>
          <div class="h-3 bg-gray-200 rounded shimmerTarget"></div>
          <div class="h-3 bg-gray-200 rounded shimmerTarget w-2/3"></div>
        </div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <EmptyCoupons
      v-else
      @refresh="() => $bus.emit($constants.EVENTS.COUPON.LIST_ALL.REFRESH)"
    />
  </PageLayout>
</template>
